import { useState } from 'react';
import { Check, Copy, Info, X } from 'lucide-react';

/**
 * "How to let us reach your MySQL server" — the setup guide behind the (i) on the BYO-DB fields.
 *
 * WHY IT IS AN EXPANDING PANEL AND NOT A HOVER TOOLTIP
 * ----------------------------------------------------
 * Hover does not exist on a phone, and its contents cannot be selected — so a tooltip would be
 * invisible to half the people who need it, and the two most useful things here (our IP address
 * and the GRANT statement) could not be copied out. Both are one tap away instead.
 *
 * WHY IT IS BILINGUAL
 * -------------------
 * The person who actually has to open the firewall is usually the shop owner or their local IT
 * provider in Bangladesh. This is the one screen where misreading an instruction means the setup
 * silently fails and they telephone instead. The technical tokens — the IP, `bind-address`, the
 * SQL — stay in English on purpose: they are typed literally, and translating them would break
 * the copy-paste.
 *
 * Ordered by how often each step is the actual culprit: a dropped connection (`ETIMEDOUT`) is
 * nearly always the firewall, so that is step 2, not step 4.
 */

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch {
          /* clipboard blocked — the text is still selectable */
        }
      }}
      className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-md border border-zinc-300 bg-white text-xs font-medium text-zinc-700
                 transition duration-100 active:scale-[0.97] hover:bg-zinc-50
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand
                 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 dark:focus-visible:ring-brand-dm"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function Snippet({ text, label }: { text: string; label: string }) {
  return (
    <div className="mt-1.5 rounded-md border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900/60">
      <div className="flex items-start gap-2 p-2">
        {/* overflow-x-auto so a long GRANT scrolls inside its own box instead of widening the page */}
        <pre className="flex-1 min-w-0 overflow-x-auto text-[11px] leading-relaxed text-zinc-800 dark:text-zinc-200 font-mono whitespace-pre">
          {text}
        </pre>
        <CopyButton text={text} label={label} />
      </div>
    </div>
  );
}

function Step({ n, en, bn, children }: { n: number; en: string; bn: string; children?: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className="flex-shrink-0 w-6 h-6 rounded-full bg-brand/10 text-brand dark:bg-brand-dm/15 dark:text-brand-dm grid place-items-center text-xs font-semibold"
      >
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-zinc-800 dark:text-zinc-100">{en}</p>
        <p lang="bn" className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">{bn}</p>
        {children}
      </div>
    </li>
  );
}

export function ByoDbGuide({ sourceIp, onClose }: { sourceIp: string; onClose: () => void }) {
  // ⚠️ The user is granted from '%' rather than pinned to our IP ON PURPOSE. The FIREWALL is
  // the gate that actually keeps strangers out; pinning the MySQL grant to our address as well is
  // a second lock that mostly doubles the repair work. If our server address ever changes, the
  // customer then has ONE thing to update — a firewall rule — instead of also logging into MySQL
  // to recreate the user. Decision: Sayem, 2026-08-30.
  const grantSql = [
    "CREATE USER 'youruser'@'%' IDENTIFIED BY 'your-password';",
    "GRANT ALL PRIVILEGES ON `yourdb`.* TO 'youruser'@'%';",
    'FLUSH PRIVILEGES;',
  ].join('\n');

  return (
    <div className="mt-3 rounded-lg border border-brand/30 dark:border-brand-dm/30 bg-brand/[0.03] dark:bg-brand-dm/[0.06] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">
            Preparing your MySQL server
          </h3>
          <p lang="bn" className="text-xs text-zinc-600 dark:text-zinc-300">
            আপনার MySQL সার্ভার প্রস্তুত করা
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the setup guide"
          className="flex-shrink-0 grid place-items-center w-9 h-9 rounded-md text-zinc-500 transition duration-100 active:scale-[0.97]
                     hover:bg-zinc-200/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand
                     dark:text-zinc-400 dark:hover:bg-zinc-700/60 dark:focus-visible:ring-brand-dm"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* The address is the single most important thing on this panel, so it is not buried
          inside a paragraph — it is the first thing, and it is copyable in one tap. */}
      <div className="mt-3 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3">
        <div className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400 font-medium">
          Our server address · আমাদের সার্ভারের আইপি
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <code className="text-sm font-mono font-semibold text-zinc-950 dark:text-zinc-50">{sourceIp}</code>
          <CopyButton text={sourceIp} label="Copy our server IP address" />
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
          Your database server must accept connections from this address.
          <span lang="bn" className="block">এই আইপি থেকে সংযোগ নেওয়ার অনুমতি আপনার ডেটাবেজ সার্ভারে দিতে হবে।</span>
        </p>
      </div>

      <ol className="mt-4 space-y-3.5">
        <Step
          n={1}
          en="Create a new, empty database and a MySQL user for it. Leave it empty — we create the POS tables inside it, and we never write over a database that already has data."
          bn="একটি নতুন, খালি ডেটাবেজ এবং তার জন্য একজন MySQL ইউজার তৈরি করুন। ডেটাবেজটি খালি রাখুন — POS-এর টেবিলগুলো আমরা তৈরি করে দেব, এবং যে ডেটাবেজে আগে থেকে ডেটা আছে তাতে আমরা কখনো লিখি না।"
        />
        <Step
          n={2}
          en="Allow our address through the firewall, on your MySQL port (usually 3306). This is a firewall or security-group rule at your hosting provider — most “connection timed out” failures are this and nothing else."
          bn="আপনার ফায়ারওয়ালে MySQL পোর্ট (সাধারণত ৩৩০৬) আমাদের আইপির জন্য খুলে দিন। এটি আপনার হোস্টিং প্রোভাইডারের ফায়ারওয়াল সেটিং — “connection timed out” সমস্যার বেশিরভাগই শুধু এই কারণেই হয়।"
        >
          <p className="mt-1.5 text-[11px] text-zinc-600 dark:text-zinc-300">
            <span className="font-medium">On cPanel:</span> Databases → <span className="font-mono">Remote MySQL</span> → add{' '}
            <span className="font-mono">{sourceIp}</span>.
            <span lang="bn" className="block">
              cPanel-এ: Databases → <span className="font-mono">Remote MySQL</span> → <span className="font-mono">{sourceIp}</span> যোগ করুন।
            </span>
          </p>
        </Step>
        <Step
          n={3}
          en="Let MySQL listen for outside connections. In my.cnf (or mysqld.cnf) set bind-address = 0.0.0.0 and restart MySQL. While it is 127.0.0.1, MySQL only answers the server it runs on."
          bn="MySQL যেন বাইরের সংযোগ গ্রহণ করে তা নিশ্চিত করুন। my.cnf (বা mysqld.cnf) ফাইলে bind-address = 0.0.0.0 দিন এবং MySQL রিস্টার্ট করুন। এটি 127.0.0.1 থাকা অবস্থায় MySQL শুধু নিজের সার্ভার থেকেই সংযোগ নেয়।"
        >
          <Snippet text="bind-address = 0.0.0.0" label="Copy the bind-address setting" />
        </Step>
        <Step
          n={4}
          en="Let the user connect remotely. A user created as 'you'@'localhost' cannot be used from outside the server, however correct the password is. Run this on your MySQL server, replacing the names with yours — step 2's firewall is what restricts who can actually reach it:"
          bn="ইউজারটিকে রিমোট সংযোগের অনুমতি দিন। 'you'@'localhost' হিসেবে তৈরি ইউজার দিয়ে সার্ভারের বাইরে থেকে সংযোগ হয় না, পাসওয়ার্ড সঠিক হলেও নয়। নিচের কমান্ডগুলো আপনার MySQL সার্ভারে চালান, নামগুলো নিজের দিয়ে বদলে নিন — কে আসলে সংযোগ করতে পারবে তা ২ নম্বর ধাপের ফায়ারওয়ালই ঠিক করে:"
        >
          <Snippet text={grantSql} label="Copy the SQL that grants access" />
        </Step>
      </ol>

      <p className="mt-3 text-[11px] text-zinc-500 dark:text-zinc-400">
        Step 2 is what keeps strangers out — the database user above may connect from anywhere, so
        the firewall rule is doing the real work. Keeping it that way means that if our server
        address ever changes, only that one rule needs updating.
        <span lang="bn" className="block">
          ২ নম্বর ধাপটিই বাইরের লোককে আটকায় — উপরের ডেটাবেজ ইউজারটি যেকোনো জায়গা থেকে সংযোগ করতে
          পারে, তাই আসল সুরক্ষা ফায়ারওয়ালের নিয়মেই। এভাবে রাখলে আমাদের সার্ভারের ঠিকানা কখনো বদলালে
          শুধু ওই একটি নিয়মই বদলাতে হবে।
        </span>
      </p>

      <p className="mt-3 text-[11px] text-zinc-500 dark:text-zinc-400">
        When all four are done, press <span className="font-medium">Test connection</span> — it tells you exactly which step is still missing.
        <span lang="bn" className="block">
          চারটি ধাপ শেষ হলে <span className="font-medium">Test connection</span> চাপুন — কোন ধাপটি এখনো বাকি আছে তা এটি বলে দেবে।
        </span>
      </p>
    </div>
  );
}

/** The (i) control that opens the guide. Exported so the field group can place it inline. */
export function ByoDbGuideToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={open ? 'Hide the MySQL setup guide' : 'How do I set up my MySQL server?'}
      className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-md border border-zinc-300 bg-white text-xs font-medium text-zinc-700
                 transition duration-100 active:scale-[0.97] hover:bg-zinc-50
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand
                 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 dark:focus-visible:ring-brand-dm"
    >
      <Info className="w-3.5 h-3.5 text-brand dark:text-brand-dm" />
      Setup guide
    </button>
  );
}

/**
 * Bangla for each failure the server can report, keyed by its stable `reason` slug.
 *
 * Kept beside the guide rather than in the field group so all customer-facing copy for this
 * feature lives in one file. `fix` takes the IP because three of these have to name it — and a
 * fix that says "allow our server" without saying which address is not a fix.
 */
export const FAILURE_BN = {
  firewall_timeout: {
    cause: 'আপনার সার্ভারে পৌঁছানো গেছে কিন্তু কোনো উত্তর আসেনি — সংযোগটি প্রত্যাখ্যান করা হয়নি, ফেলে দেওয়া হয়েছে, যা সাধারণত ফায়ারওয়ালের কাজ।',
    fix: (ip: string) => `ডেটাবেজ পোর্টটি আপনার ফায়ারওয়ালে ${ip} এর জন্য খুলে দিন। এটি সাধারণত হোস্টিং প্রোভাইডারের ফায়ারওয়াল সেটিং, MySQL-এর সেটিং নয়।`,
  },
  connection_refused: {
    cause: 'আপনার সার্ভার উত্তর দিয়ে সংযোগ প্রত্যাখ্যান করেছে — অর্থাৎ ফায়ারওয়াল ঠিক আছে, কিন্তু MySQL বাইরের সংযোগ গ্রহণ করছে না।',
    fix: () => 'my.cnf (বা mysqld.cnf) ফাইলে bind-address = 0.0.0.0 দিন এবং MySQL রিস্টার্ট করুন। পোর্ট নম্বরটিও মিলিয়ে নিন — সাধারণত ৩৩০৬।',
  },
  host_not_found: {
    cause: 'এই হোস্টনেমটি কোনো ঠিকানায় রিজলভ হচ্ছে না।',
    fix: () => 'বানান মিলিয়ে দেখুন। ডোমেইনটি নতুন হলে DNS ছড়াতে সময় লাগতে পারে — সার্ভারের পাবলিক আইপি দিলেও চলবে।',
  },
  host_not_allowed: {
    cause: 'MySQL-এ পৌঁছানো গেছে, কিন্তু আমাদের ঠিকানা থেকে এই অ্যাকাউন্টের সংযোগের অনুমতি নেই।',
    fix: (ip: string) => `${ip} থেকে ইউজারটিকে অনুমতি দিন — সেটআপ গাইডের ৪ নম্বর ধাপের কমান্ডগুলো চালান।`,
  },
  access_denied: {
    cause: 'MySQL-এ পৌঁছানো গেছে, কিন্তু ইউজারনেম বা পাসওয়ার্ড গ্রহণ করা হয়নি — অথবা অ্যাকাউন্টটি শুধু সার্ভারের নিজের জন্য তৈরি।',
    fix: (ip: string) => `পাসওয়ার্ড মিলিয়ে দেখুন, তারপর নিশ্চিত করুন ইউজারটি ${ip} থেকে সংযোগ করতে পারে — 'localhost' দিয়ে তৈরি ইউজার রিমোটে কাজ করে না।`,
  },
  database_missing: {
    cause: 'MySQL-এ সংযোগ হয়েছে, কিন্তু এই নামের ডেটাবেজ সেখানে নেই।',
    fix: () => 'ঠিক এই নামে একটি খালি ডেটাবেজ আগে তৈরি করুন — ভেতরের টেবিলগুলো আমরা বানিয়ে দেব, কিন্তু ডেটাবেজটি আমরা তৈরি করি না।',
  },
  no_rights_on_database: {
    cause: 'ইউজার লগইন করতে পেরেছে, কিন্তু এই ডেটাবেজে তার কোনো অনুমতি নেই।',
    fix: () => 'GRANT ALL PRIVILEGES ON yourdb.* কমান্ডটি চালান, তারপর FLUSH PRIVILEGES দিন।',
  },
  auth_plugin: {
    cause: 'আপনার সার্ভারের অথেনটিকেশন পদ্ধতিটি আমরা ব্যবহার করতে পারছি না।',
    fix: () => "ইউজারটি সাধারণ পদ্ধতিতে তৈরি করুন: ALTER USER 'youruser'@'%' IDENTIFIED WITH mysql_native_password BY '…';",
  },
  network_reset: {
    cause: 'সংযোগ শুরু হয়ে হঠাৎ বন্ধ হয়ে গেছে।',
    fix: () => 'আবার চেষ্টা করুন, এবং দেখুন ডেটাবেজ পোর্টের সামনে কোনো প্রক্সি বা টাইমআউট সেটিং আছে কিনা।',
  },
  unknown: {
    cause: 'ডেটাবেজে সংযোগ করা যায়নি।',
    fix: (ip: string) => `ঠিকানা, পোর্ট, ইউজারনেম ও পাসওয়ার্ড মিলিয়ে দেখুন, এবং ${ip} থেকে সংযোগের অনুমতি আছে কিনা নিশ্চিত করুন।`,
  },
} as const;
